import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {RGBA} from '@Utils/color.util';
import {deviceType, isAndroid, isFirefox, isIOS, isMacOs, isOpera, isSafari, isSamsungBrowser} from 'react-device-detect';
import Mousetrap from 'mousetrap';
import {Canvas} from '@postermywall/fabricjs-2';
import styles from './canvas-color-picker.module.scss';
import {CURSOR_OVERLAY_CONTAINER_ID} from '@Components/poster-editor/components/cursor-overlay/cursor-overlay.types';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {setCanvasCursor} from '@Utils/setCanvasCursor';

const DEVICE_TYPE = {
  MOBILE: 'mobile',
};

const CURSOR_OFFSET = 45;

interface CanvasColorPickerProps {
  color: RGBA;
  onChangeColor(color: RGBA | null): void;
  onChangeEnded(color: RGBA | null): void;
  className?: string;

  onChangeColorPickerMode?(state: boolean): void;
}

const getAllFabricCanvases = (): Canvas[] => {
  const pages = window.posterEditor?.whiteboard?.pages.getPagesInOrder() ?? [];
  return pages.map((page) => page.fabricCanvas).filter((canvas): canvas is Canvas => canvas instanceof Canvas);
};

const getCanvasFromEvent = (e: CanvasEvents['mouse:move']): Canvas | null => {
  const nativeTarget = e.e.target;
  if (!(nativeTarget instanceof HTMLCanvasElement)) {
    return null;
  }

  const canvases = getAllFabricCanvases();
  for (const canvas of canvases) {
    if (canvas.upperCanvasEl === nativeTarget || canvas.getElement() === nativeTarget) {
      return canvas;
    }
  }

  return null;
};

export function CanvasColorPicker({className = '', onChangeColorPickerMode = (): void => {}, ...props}: CanvasColorPickerProps): ReactElement {
  const [isColorPickerEnabled, setIsColorPickerEnabled] = useState(false);
  const colorBeforePickerIsEnabled = useRef<RGBA>(undefined);

  const onClickPicker = (): void => {
    if (!isColorPickerEnabled) {
      enableColorPicker();
      setIsColorPickerEnabled(true);
      onChangeColorPickerMode(true);
    } else {
      disableColorPickMode();
    }
  };

  const isCustomCursorPositioningUnavailable = (): boolean => {
    return (isSafari && (isIOS || isMacOs)) || isSamsungBrowser || isOpera || (isFirefox && isAndroid);
  };

  const getColorFromCoOrdinates = (e: CanvasEvents['mouse:move']): RGBA | null => {
    const sourceCanvas = e.target?.canvas ?? getCanvasFromEvent(e);
    if (!sourceCanvas) {
      return null;
    }

    const pointer = e.viewportPoint;
    const retinaScaling = sourceCanvas.getRetinaScaling();
    const p = sourceCanvas
      .getContext()
      .getImageData(pointer.x * retinaScaling, pointer.y * retinaScaling + (isCustomCursorPositioningUnavailable() ? CURSOR_OFFSET : 0), 1, 1).data;
    return [p[0], p[1], p[2], 1];
  };

  const onColorPicking = (e: CanvasEvents['mouse:move']): void => {
    const color = getColorFromCoOrdinates(e);
    props.onChangeColor(color);
  };

  const onColorPicked = (e: CanvasEvents['mouse:move']): void => {
    const color = getColorFromCoOrdinates(e);
    props.onChangeEnded(color);
    disableColorPickMode();
  };

  const onRevertColor = (): void => {
    if (colorBeforePickerIsEnabled.current) {
      props.onChangeColor(colorBeforePickerIsEnabled.current);
    }
  };

  const disableColorPickMode = (): void => {
    const canvases = getAllFabricCanvases();
    for (const canvas of canvases) {
      canvas.off('mouse:move', onColorPicking);
      canvas.off('mouse:down', onColorPicked);
      canvas.off('mouse:out', onRevertColor);
      setCanvasCursor(canvas, 'default');
      canvas.setCursor('default');
    }

    const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
    cursorOverlay?.removeEventListener('click', disableColorPickMode);
    Mousetrap.unbind('escape');

    setIsColorPickerEnabled(false);
    onChangeColorPickerMode(false);
  };

  useEffect(() => {
    return (): void => {
      if (isColorPickerEnabled) {
        disableColorPickMode();
      }
    };
  }, []);

  const onPressEscapeOverloadKey = (): void => {
    disableColorPickMode();
    onRevertColor();
  };

  const enableColorPicker = (): void => {
    colorBeforePickerIsEnabled.current = props.color;
    const colorPickerCursor = `url("${window.PMW.util.asset_url('images/postermaker/color-picker.ico')}") 0 25 , auto`;

    const canvases = getAllFabricCanvases();
    for (const canvas of canvases) {
      setCanvasCursor(canvas, colorPickerCursor);
      canvas.setCursor(colorPickerCursor);
      canvas.on('mouse:move', onColorPicking);
      canvas.on('mouse:down', onColorPicked);
      canvas.on('mouse:out', onRevertColor);
    }

    const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
    cursorOverlay?.addEventListener('click', disableColorPickMode);

    if (deviceType !== DEVICE_TYPE.MOBILE) {
      Mousetrap.bind('escape', onPressEscapeOverloadKey);
    }
  };

  return (
    <Icon
      onClick={onClickPicker}
      className={`${styles.pickerIcon} spacing-m-l-2`}
      icon="icon-colorpicker"
      size={IconSize.SIZE_ICON_20}
      shape={IconShape.SQUARE}
      type={IconType.SECONDARY}
    />
  );
}
